import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import MUIDataTable from 'mui-datatables';
import { ThemeProvider, StyledEngineProvider, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import addDays from 'date-fns/addDays';

import { LinkButton } from 'ui/Buttons';
import { formatDate, formatCurrency } from 'shared/functions';

import { ShopToolbar } from '../ShopToolbar';
import { ShopSelectionListAction } from './ShopSelectionListActions';

const getMuiTheme = () => ({
  component: {
    MUIDataTableHeadCell: {
      styleOverrides: {
        fixedHeader: {
          zIndex: 1,
        },
      }
    },
    MUIDataTableSelectCell: {
      styleOverrides: {
        headerCell: {
          zIndex: 1,
        },
        fixedLeft: {
          zIndex: 1,
        }
      }
    },
    MUIDataTable: {
      styleOverrides: {
        paper: {
          boxShadow: 'none',
        }
      }
    },
    MUIDataTableToolbarSelect: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          height: 64,
          backgroundColor: '#fff',
        },
        title: {
          minWidth: 200,
        }
      }
    },
    MUIDataTableSearch: {
      styleOverrides: {
        main: {
          '& input': {
            padding: '8px 10px',
          },
        },
        searchText: {
          marginTop: 8,
        },
        searchIcon: {
          marginTop: 15,
        }
      }
    },
  },
});

const useStyles = makeStyles(theme => ({
  toolbarContainer: {
    marginRight: theme.spacing(3),
  },
}));

const ShopSelectionList = ({
  products,
  columns,
  onSelectionChange,
  actions,
  toolbar,
  options,
  editMode,
  showFreeTrialPrice,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [selectedProducts, setSelectedProducts] = useState([]);

  const handleRowSelect = (_currentSelection, items) => {
    const selectedItems = items.map(({ dataIndex }) => dataIndex);

    setSelectedProducts(selectedItems);

    onSelectionChange(
      products.filter((_product, index) => selectedItems.includes(index)).map(({ id }) => id),
    );
  };

  const columnsSetup = [
    {
      name: 'id',
      label: t('ID'),
      options: {
        display: columns.includes('id'),
      },
    },
    {
      name: 'name',
      label: t('Name'),
      options: {
        display: columns.includes('name'),
        sort: true,
        customBodyRender: (value, { rowData }) => (
          <LinkButton
            to={
              editMode ? `/subscriptions/${rowData[0]}` : `/subscriptions?productId=${rowData[0]}`
            }
            label={value}
          />
        ),
      },
    },
    {
      name: 'categories',
      label: t('Category'),
      options: {
        display: columns.includes('category'),
        sort: true,
        customBodyRender: value => (value ? value.join(', ') : ''),
      },
    },
    {
      name: 'regions',
      label: t('Region'),
      options: {
        display: columns.includes('region'),
        sort: true,
        customBodyRender: value => (value ? value.join(', ') : ''),
      },
    },
    {
      name: 'segments',
      label: t('Segment'),
      options: {
        display: columns.includes('segment'),
        sort: true,
        customBodyRender: value => (value ? value.join(', ') : ''),
      },
    },
    {
      name: 'productLines',
      label: t('Product line'),
      options: {
        display: columns.includes('productLines'),
        sort: true,
        customBodyRender: value => (value ? value.join(', ') : ''),
      },
    },
    {
      name: 'industries',
      label: t('Industry'),
      options: {
        display: columns.includes('industry'),
        sort: true,
        customBodyRender: value => (value ? value.map(({ name }) => name).join(', ') : ''),
      },
    },
    {
      name: 'sources',
      label: t('Source'),
      options: {
        display: columns.includes('source'),
        sort: true,
      },
    },
    {
      name: 'purchaseDate',
      label: t('Date of purchase'),
      options: {
        display: columns.includes('purchaseDate'),
        sort: true,
        customBodyRender: value => formatDate(value),
      },
    },
    {
      name: 'cancellationPeriod',
      label: t('Cancel by'),
      options: {
        display: columns.includes('cancellationPeriod'),
        sort: true,
        customBodyRender: (cancellationPeriod, { rowData }) => {
          const today = new Date();

          const subscriptionUntil = rowData[11];
          const subscriptionUntilDate = new Date(subscriptionUntil);

          if (!subscriptionUntil || today > subscriptionUntilDate) {
            return '-';
          }

          const cancelDate = addDays(new Date(subscriptionUntilDate), -cancellationPeriod);

          return formatDate(cancelDate);
        },
      },
    },
    {
      name: 'subscriptionUntil',
      label: t('Renewal / end'),
      options: {
        display: columns.includes('subscriptionUntil'),
        sort: true,
        customBodyRender: value => (value ? formatDate(value) : '-'),
      },
    },
    {
      name: 'amount',
      label: t('Amount'),
      options: {
        display: columns.includes('amount'),
        sort: true,
      },
    },
    {
      name: 'pricing',
      label: t('Price'),
      options: {
        display: columns.includes('pricing'),
        sort: true,
        customBodyRender: (priceData, { rowData }) => {
          if (!priceData) return '-';

          const pricing = priceData.find(({ active }) => active);

          if (!pricing) {
            return '-';
          }

          const trialStarted = rowData[16];

          if (!showFreeTrialPrice && trialStarted) {
            return t('Free trial');
          }

          return pricing.price === 0 ? t('Free of charge') : formatCurrency(pricing.price, 'EUR');
        },
      },
    },
    {
      name: 'active',
      label: t('Active'),
      options: {
        display: columns.includes('active'),
        sort: true,
        customBodyRender: value => (value ? t('Yes') : t('No')),
      },
    },
    {
      name: 'actions',
      label: t('Actions'),
      options: {
        display: columns.includes('actions'),
        sort: true,
        customBodyRender: (_, { rowData }) => <ShopSelectionListAction productId={rowData[0]} />,
      },
    },
    {
      name: 'trialStarted',
      label: t('Free trial started'),
      options: {
        display: columns.includes('trialStarted'),
        sort: true,
        customBodyRender: value => (value ? t('Yes') : t('No')),
      },
    },
    {
      name: 'trialPeriod',
      label: t('Trial period'),
      options: {
        display: columns.includes('trialPeriod'),
      },
    },
  ];

  const optionsSetup = {
    filterType: 'checkbox',
    print: false,
    download: false,
    filter: false,
    search: false,
    onRowsDelete: false,
    viewColumns: false,
    pagination: false,
    sortDirection: 'desc',
    onRowsSelect: handleRowSelect,
    rowsSelected: selectedProducts,
    customToolbar: () => toolbar,
    customToolbarSelect: () => (
      <Grid container justifyContent="flex-end" spacing={1} className={classes.toolbarContainer}>
        {toolbar && <Grid item>{toolbar}</Grid>}
        {actions.length !== 0 && (
          <Grid item>
            <ShopToolbar actions={actions} />
          </Grid>
        )}
      </Grid>
    ),
    ...options,
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={getMuiTheme()}>
        <MUIDataTable data={products} columns={columnsSetup} options={optionsSetup} />
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

ShopSelectionList.defaultProps = {
  onSelectionChange: () => {},
  actions: [],
  toolbar: null,
  options: {},
  editMode: false,
  showFreeTrialPrice: true,
};

ShopSelectionList.propTypes = {
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      categories: PropTypes.arrayOf(PropTypes.string).isRequired,
      regions: PropTypes.arrayOf(PropTypes.string).isRequired,
      segments: PropTypes.arrayOf(PropTypes.string).isRequired,
      productLines: PropTypes.arrayOf(PropTypes.string).isRequired,
      duration: PropTypes.string,
      industries: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          name: PropTypes.string.isRequired,
        }),
      ).isRequired,
      sources: PropTypes.arrayOf(PropTypes.string).isRequired,
      purchaseDate: PropTypes.string,
      cancelPeriod: PropTypes.string,
      subscriptionUntil: PropTypes.string,
      amount: PropTypes.number,
      pricing: PropTypes.arrayOf(
        PropTypes.shape({
          price: PropTypes.number.isRequired,
          activationFee: PropTypes.number,
          tier: PropTypes.number,
        }),
      ),
    }),
  ).isRequired,
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectionChange: PropTypes.func,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      action: PropTypes.func.isRequired,
    }),
  ),
  toolbar: PropTypes.node,
  options: PropTypes.object,
  editMode: PropTypes.bool,
  showFreeTrialPrice: PropTypes.bool,
};

export { ShopSelectionList };
